import { isNonProd } from "../envGate";

// FriendlyError — DS feedback primitive for surfacing a failure to the end user
// WITHOUT leaking raw API error text in production.
//
//   • ALWAYS shows a reassuring, environment-independent friendly line.
//   • In non-prod (staging + local) ONLY, additionally renders the raw `detail`
//     in a visually-subdued, monospace block so engineers can debug. In prod the
//     detail is never rendered into the DOM.
//
// Env is read from the canonical `isNonProd()` gate (utils/envGate.ts) — never
// re-derive prod/non-prod from URLs here. Reuses the danger error-box styling
// shared with the scheduling modal's inline error.
//
// NOTE: "the team has been notified" is product copy only — there is no
// error-reporting/telemetry wiring behind it today.

export const FRIENDLY_ERROR_MESSAGE = "Something went wrong — the team has been notified.";

export interface FriendlyErrorProps {
    /** Raw error text. Shown (de-emphasized) in non-prod only; never in prod. */
    detail?: string | null;
}

export function FriendlyError({ detail }: FriendlyErrorProps) {
    // Null-safe: `null`/`undefined` means "no error" ⇒ render nothing. An empty
    // string still renders the friendly line (just no details block); only the
    // `showDetail` gate below suppresses the per-error detail for empty strings.
    if (detail == null) return null;

    const showDetail = isNonProd() && detail.trim() !== "";

    return (
        <div
            role="alert"
            className="rounded-[var(--radius-sm)] border border-danger-bd bg-danger-bg px-4 py-4 font-sans text-xs text-danger"
        >
            <p className="leading-[1.45]">{FRIENDLY_ERROR_MESSAGE}</p>
            {showDetail && (
                <div className="mt-2 border-t border-danger-bd pt-2">
                    <p className="text-xs uppercase tracking-wide text-tertiary">
                        Details (staging/local only):
                    </p>
                    <pre className="mt-1 whitespace-pre-wrap break-words font-mono text-xs leading-[1.5] text-tertiary">
                        {detail}
                    </pre>
                </div>
            )}
        </div>
    );
}
